// Constants
import { CARE_PLAN_PAGINATION_COUNT } from "@benchmark/core/constants";

// Hooks
import { useCallback, useEffect, useRef, useState } from "react";
import { useMedplum } from "@medplum/react";
import { getStatus, OperationOutcomeError } from "@medplum/core";

// Types
import type { CarePlan } from "@medplum/fhirtypes";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";
import { toLogSafeError } from "@benchmark/core/utils";

/**
 * Keep useful error categories without forwarding patient-linked diagnostics.
 * @agentic connections
 */
function logCarePlanError(
  error: unknown,
  context: "Error fetching care plans." | "Error in fetchCarePlans effect.",
): void {
  // The shared helper forwards message/name, so sanitize before passing them.
  const sanitized = new Error(context);
  if (error instanceof OperationOutcomeError) {
    sanitized.name = "OperationOutcomeError";
  } else if (error instanceof TypeError) {
    sanitized.name = "TypeError";
  } else if (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException
  ) {
    sanitized.name = "DOMException";
  } else {
    sanitized.name = error instanceof Error ? "Error" : "Unknown";
  }

  const metadata = toLogSafeError(sanitized);
  console.error(
    context,
    error instanceof OperationOutcomeError
      ? { ...metadata, outcomeStatus: getStatus(error.outcome) }
      : metadata,
  );
}

/**
 * Result type for usePatientCarePlans hook. Multi-plan-aware: a patient may
 * hold zero-to-many active Triage care plans alongside an optional CoCM plan.
 *
 * `cocmCarePlan` and `triageCarePlans` are scoped to active/draft only
 * (matching the `activeCarePlans` filter), so consumers don't have to
 * re-filter and don't risk picking up a stale completed/revoked plan that
 * sorts ahead of the active one in `-date` order.
 * @agentic @benchmark/core
 */
export type UsePatientCarePlansResult = {
  carePlans: CarePlan[];
  activeCarePlans: CarePlan[];
  intakeCarePlan: CarePlan | undefined;
  /** Active or draft CoCM CarePlan (D13: at most one). */
  cocmCarePlan: CarePlan | undefined;
  /** Active or draft Triage CarePlans (D2: zero-to-many). */
  triageCarePlans: CarePlan[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

/**
 * Fetches the existing first page for operational consumers. Profile consent
 * opts into complete history without making other workflows depend on it.
 * Subscribes to care plan events for refetch and filters active/intake/triage.
 * @agentic @benchmark/core
 */
export function usePatientCarePlans(
  patientId?: string,
  { fetchAllPages = false }: { fetchAllPages?: boolean } = {},
): UsePatientCarePlansResult {
  const medplum = useMedplum();
  const [carePlans, setCarePlans] = useState<CarePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [resolvedContext, setResolvedContext] = useState<{
    patientId: string;
    medplum: typeof medplum;
    fetchAllPages: boolean;
  }>();

  // Deduplicate only within one patient/client context. The token also stops
  // obsolete requests from publishing results or clearing a newer request.
  const fetchPromiseRef = useRef<{
    patientId: string;
    medplum: typeof medplum;
    fetchAllPages: boolean;
    token: symbol;
    promise: Promise<void>;
  } | null>(null);
  const activeContextRef = useRef<{
    patientId?: string;
    medplum: typeof medplum;
    fetchAllPages: boolean;
  } | null>(null);

  const fetchCarePlans = useCallback(async () => {
    // Async workflows can retain a callback after navigation or unmount.
    const activeContext = activeContextRef.current;
    if (
      !activeContext ||
      activeContext.patientId !== patientId ||
      activeContext.medplum !== medplum ||
      activeContext.fetchAllPages !== fetchAllPages
    ) {
      return;
    }

    if (!patientId) {
      fetchPromiseRef.current = null;
      setCarePlans([]);
      setLoading(false);
      setError(null);
      setResolvedContext(undefined);
      return;
    }

    const inFlight = fetchPromiseRef.current;
    if (
      inFlight?.patientId === patientId &&
      inFlight.medplum === medplum &&
      inFlight.fetchAllPages === fetchAllPages
    ) {
      return inFlight.promise;
    }

    setLoading(true);
    setError(null);

    const token = Symbol();
    const isCurrentRequest = () => fetchPromiseRef.current?.token === token;

    // Register the request before invoking Medplum, including when the client
    // throws synchronously. Cleanup can also cancel this queued work.
    const fetchPromise = Promise.resolve().then(async () => {
      if (!isCurrentRequest()) return;
      try {
        const list: CarePlan[] = [];
        for await (const page of medplum.searchResourcePages("CarePlan", {
          subject: `Patient/${patientId}`,
          _sort: "-date",
          _count: CARE_PLAN_PAGINATION_COUNT.toString(),
        })) {
          if (!isCurrentRequest()) return;
          list.push(...page);
          if (!fetchAllPages) break;
        }

        // History readers publish only after every page succeeds. Operational
        // readers retain their bounded lookup, independent of history failures.
        if (!isCurrentRequest()) return;
        setCarePlans(list);
      } catch (err) {
        if (!isCurrentRequest()) return;
        logCarePlanError(err, "Error fetching care plans.");
        // Wrap non-Error exceptions in Error object for consistent error handling
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setCarePlans([]);
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
          setResolvedContext({ patientId, medplum, fetchAllPages });
          fetchPromiseRef.current = null;
        }
      }
    });

    fetchPromiseRef.current = {
      patientId,
      medplum,
      fetchAllPages,
      token,
      promise: fetchPromise,
    };
    return fetchPromise;
  }, [patientId, medplum, fetchAllPages]);

  useEffect(() => {
    let cancelled = false;
    const context = { patientId, medplum, fetchAllPages };
    activeContextRef.current = context;

    queueMicrotask(() => {
      if (cancelled) return;
      void fetchCarePlans().catch((err: unknown) => {
        if (!cancelled) {
          logCarePlanError(err, "Error in fetchCarePlans effect.");
        }
      });
    });

    return () => {
      cancelled = true;
      if (activeContextRef.current === context) {
        activeContextRef.current = null;
        fetchPromiseRef.current = null;
      }
    };
  }, [fetchCarePlans, patientId, medplum, fetchAllPages]);

  // Auto-refetch on care-plan events for this patient. The 500ms debounce
  // coalesces the burst of events a single workflow can publish (a care
  // plan create/activate transaction emits several at once).
  useEventDrivenRefetch(
    [
      DomainEventNames.CarePlanCreated,
      DomainEventNames.CarePlanActivated,
      DomainEventNames.CarePlanUpdated,
      DomainEventNames.CarePlanCompleted,
      DomainEventNames.CarePlanCancelled,
    ],
    {
      refetch: fetchCarePlans,
      enabled: !!patientId,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patientId,
      debounceMs: 500,
    },
  );

  // Hide previous-patient data on the first render after a context change,
  // before effect cleanup and the new request have had a chance to run.
  const hasCurrentResult =
    !!patientId &&
    resolvedContext?.patientId === patientId &&
    resolvedContext.medplum === medplum &&
    resolvedContext.fetchAllPages === fetchAllPages;
  const currentCarePlans = hasCurrentResult ? carePlans : [];

  const activeCarePlans = currentCarePlans.filter(
    (cp) => cp.status === "active" || cp.status === "draft",
  );

  const intakeCarePlan = currentCarePlans.find(
    (cp) => cp.category?.[0]?.coding?.[0]?.code === "intake",
  );

  // Operational accessors are scoped to active/draft so a stale completed
  // CoCM that sorts ahead in `-date` order can't shadow the truly active
  // one (Bugbot finding on PR #785).
  const cocmCarePlan = activeCarePlans.find(
    (cp) => cp.category?.[0]?.coding?.[0]?.code === "cocm",
  );

  const triageCarePlans = activeCarePlans.filter(
    (cp) => cp.category?.[0]?.coding?.[0]?.code === "triage",
  );

  const isLoadingCurrentPatient = !!patientId && (loading || !hasCurrentResult);

  return {
    carePlans: currentCarePlans,
    activeCarePlans,
    intakeCarePlan,
    cocmCarePlan,
    triageCarePlans,
    loading: isLoadingCurrentPatient,
    error: hasCurrentResult ? error : null,
    refetch: fetchCarePlans,
  };
}
